/**
 * Hospitals domain — React Query hooks.
 *
 * Post-FIR-6997 the hospitals table reads/mutates through the unified
 * opportunities engine (`services/opportunities`, `common_table_actions`),
 * so this domain's list/summary hooks and wrapped bulk mutations were
 * removed as dead code. What remains is the detail hook the hospital
 * detail pages consume.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { getHospitalQueryKey } from '../generated/hooks/hospitalController'
import client from '../apiClient'

import type {
  GetHospitalQueryResponse,
  GetHospital404,
} from '../generated/types/GetHospital'

export type {
  GetHospitalQueryResponse,
  GetHospitalPathParams,
} from '../generated/types/GetHospital'

/**
 * Get hospital detail.
 *
 * Hand-written wrapper around the generated `getHospital` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetHospital(
  hospitalId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<GetHospitalQueryResponse, AxiosError<GetHospital404>>,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:hospital_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = hospitalId != null ? String(hospitalId) : undefined
  return useQuery<GetHospitalQueryResponse, AxiosError<GetHospital404>>({
    queryKey:
      id !== undefined
        ? getHospitalQueryKey({ hospital_id: id })
        : ['hospital', hospitalId],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async ({ signal }) =>
      (
        await client<GetHospitalQueryResponse>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${id}/`,
          params: { target_type: 'hospital' },
          signal,
        })
      ).data,
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}
